import React from 'react'
import { styled } from 'baseui'
import { lightTheme } from '@/customTheme'

export type TableAlign = 'start' | 'center' | 'end'

export interface TableColumnConfig {
  key: string
  label?: React.ReactNode
  /** Track width in px. */
  width: number
  /** Cell alignment. Default: 'center'. */
  align?: TableAlign
  /** Drawn inside the highlight card; also drops this column's dividers. */
  highlighted?: boolean
}

export interface TableRowsConfig {
  /** Width of the leading row-header column, in px. */
  headerWidth: number
  /** Row-header alignment. Default: 'start'. */
  headerAlign?: TableAlign
  /** Body row minimum height. Default: 44. */
  minHeight?: number
  /** Header row minimum height. Default: 42. */
  headerMinHeight?: number
  /** CSS border shorthand. Default: 1px solid grayScale100. */
  divider?: string
}

export interface TableRow {
  key: string
  /** Leading cell content (already formatted by the caller). */
  header?: React.ReactNode
  /** Column key -> cell content. A missing key renders an empty cell. */
  cells: Record<string, React.ReactNode>
}

export interface TableHighlight {
  /** Card border color. */
  color: string
  /** Card fill. Default: transparent. */
  surface?: string
  /** Corner radius in px. Default: 12. */
  radius?: number
  /** Border width in px. Default: 2. */
  borderWidth?: number
}

export interface TableProps {
  /** Top-left cell content, above the row headers. */
  title?: React.ReactNode
  columnsConfig: TableColumnConfig[]
  rowsConfig: TableRowsConfig
  rows: TableRow[]
  /** Styling for the column(s) flagged `highlighted`. Omit for a plain table. */
  highlight?: TableHighlight
}

const DEFAULT_ROW_MIN_HEIGHT = 44
const DEFAULT_HEADER_MIN_HEIGHT = 42
const DEFAULT_HIGHLIGHT_RADIUS = 12
const DEFAULT_HIGHLIGHT_BORDER_WIDTH = 2
const DEFAULT_DIVIDER = `1px solid ${lightTheme.colors.grayScale100}`

const FLEX_ALIGN: Record<TableAlign, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
}

const TableFrame = styled('div', () => ({
  position: 'relative',
}))

/**
 * Accent frame behind a highlighted column. Stretched to the grid rather than
 * given a computed height, so it still frames the column when a wrapped label
 * makes a row taller than its minimum.
 */
const HighlightCard = styled(
  'div',
  (props: {
    $left: number
    $width: number
    $surface?: string
    $color: string
    $radius: number
    $borderWidth: number
  }) => ({
    position: 'absolute',
    top: '0',
    bottom: '0',
    left: `${props.$left}px`,
    width: `${props.$width}px`,
    background: props.$surface ?? 'transparent',
    border: `${props.$borderWidth}px solid ${props.$color}`,
    borderRadius: `${props.$radius}px`,
    boxSizing: 'border-box',
  }),
)

const Grid = styled('div', (props: { $template: string }) => ({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: props.$template,
}))

/** `display: contents` keeps a row's cells as direct grid items while still exposing a row to assistive tech. */
const Row = styled('div', () => ({
  display: 'contents',
}))

const Cell = styled(
  'div',
  (props: {
    $minHeight: number
    $align: TableAlign
    $divider: string
    $highlighted?: boolean
    $isHeaderColumn?: boolean
  }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: FLEX_ALIGN[props.$align],
    gap: '8px',
    boxSizing: 'border-box',
    minHeight: `${props.$minHeight}px`,
    padding: props.$isHeaderColumn ? '4px 8px' : '4px 0',
    borderBottom: props.$highlighted ? 'none' : props.$divider,
    // Lets content wrap inside the fixed-width track instead of forcing the
    // flex line wider than the track.
    minWidth: '0',
  }),
)

/**
 * Grid table: a leading row-header column plus one fixed-width column per entry
 * in `columnsConfig`, with optional accent framing for highlighted columns.
 *
 * Presentation only — every cell is a `ReactNode` the caller already formatted.
 * The component contributes layout, row dividers and the table semantics
 * (`table` / `row` / `columnheader` / `rowheader` / `cell`).
 */
function Table({ title, columnsConfig, rowsConfig, rows, highlight }: TableProps) {
  const {
    headerWidth,
    headerAlign = 'start',
    minHeight = DEFAULT_ROW_MIN_HEIGHT,
    headerMinHeight = DEFAULT_HEADER_MIN_HEIGHT,
    divider = DEFAULT_DIVIDER,
  } = rowsConfig

  const template = [`${headerWidth}px`, ...columnsConfig.map(column => `${column.width}px`)].join(' ')

  // Offsets are accumulated rather than multiplied so columns of differing
  // widths still line their cards up with their tracks.
  const columnOffsets: number[] = []
  columnsConfig.reduce((offset, column) => {
    columnOffsets.push(offset)
    return offset + column.width
  }, headerWidth)

  return (
    <TableFrame>
      {highlight &&
        columnsConfig.map((column, index) =>
          column.highlighted ? (
            <HighlightCard
              key={column.key}
              aria-hidden
              $left={columnOffsets[index]}
              $width={column.width}
              $surface={highlight.surface}
              $color={highlight.color}
              $radius={highlight.radius ?? DEFAULT_HIGHLIGHT_RADIUS}
              $borderWidth={highlight.borderWidth ?? DEFAULT_HIGHLIGHT_BORDER_WIDTH}
            />
          ) : null,
        )}
      <Grid role="table" $template={template}>
        <Row role="row">
          <Cell
            role="columnheader"
            $minHeight={headerMinHeight}
            $align={headerAlign}
            $divider={divider}
            $isHeaderColumn
          >
            {title}
          </Cell>
          {columnsConfig.map(column => (
            <Cell
              key={column.key}
              role="columnheader"
              $minHeight={headerMinHeight}
              $align={column.align ?? 'center'}
              $divider={divider}
              $highlighted={column.highlighted}
            >
              {column.label}
            </Cell>
          ))}
        </Row>

        {rows.map(row => (
          <Row key={row.key} role="row">
            <Cell
              role="rowheader"
              $minHeight={minHeight}
              $align={headerAlign}
              $divider={divider}
              $isHeaderColumn
            >
              {row.header}
            </Cell>
            {columnsConfig.map(column => (
              <Cell
                key={column.key}
                role="cell"
                $minHeight={minHeight}
                $align={column.align ?? 'center'}
                $divider={divider}
                $highlighted={column.highlighted}
              >
                {row.cells[column.key]}
              </Cell>
            ))}
          </Row>
        ))}
      </Grid>
    </TableFrame>
  )
}

export default Table
